import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import PIFForm from '../../components/forms/PIFForm';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';

export default function PIFFormPage() {
  const { fundId, streamId } = useParams();
  const [fund, setFund] = useState(null);
  const [stream, setStream] = useState(null);
  const [rubric, setRubric] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const getConfig = httpsCallable(functions, 'getPublicPIFConfig');
        const result = await getConfig({ fundId, streamId: streamId || null });
        const { fund: fundData, stream: streamData, rubric: rubricData } = result.data;

        if (!fundData) { setError('Fund not found.'); return; }
        setFund(fundData);
        setStream(streamData);

        if (!rubricData) {
          setError('Applications are not currently open for this fund.');
          return;
        }
        setRubric(rubricData);
      } catch (e) {
        if (e.code === 'not-found' || e.message?.includes('not found')) {
          setError('Applications are not currently open for this fund.');
        } else {
          setError('Unable to load the application form. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [fundId, streamId]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <LoadingSpinner />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '40px 32px', maxWidth: 480, textAlign: 'center', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a2744', marginBottom: 8 }}>Applications Not Open</h2>
        <p style={{ fontSize: 15, color: '#64748b' }}>{error}</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9' }}>
      {/* Header */}
      <div style={{ background: '#1a2744', padding: '20px 0' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px' }}>
          <p style={{ fontSize: 12, color: '#c9952a', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
            Mirepa Investment Advisors
          </p>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>
            {stream ? `${fund.name} — ${stream.name}` : fund.name}
          </h1>
          <p style={{ fontSize: 14, color: '#a8b4c8', marginTop: 4 }}>
            Investment Application Form
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>
        {!started ? (
          <div style={{
            background: '#fff',
            borderRadius: 10,
            padding: '40px 32px',
            border: '1px solid #e2e8f0',
            textAlign: 'center',
          }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a2744', marginBottom: 12 }}>
              Apply for Investment
            </h2>
            <p style={{ fontSize: 15, color: '#64748b', lineHeight: 1.7, marginBottom: 24, maxWidth: 480, margin: '0 auto 24px' }}>
              Welcome to the {fund.name} application process. Please complete this form fully and
              accurately. All questions marked with an asterisk (*) are required.
            </p>
            <div style={{
              background: '#f8f9fa',
              borderRadius: 8,
              padding: '16px 20px',
              marginBottom: 28,
              textAlign: 'left',
            }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1a2744', marginBottom: 8 }}>Before you begin:</p>
              <ul style={{ fontSize: 14, color: '#64748b', lineHeight: 2, paddingLeft: 20, margin: 0 }}>
                <li>This form will take approximately 15–20 minutes to complete</li>
                <li>You can upload supporting documents (PDF, Word, Excel, max 20MB each)</li>
                <li>Your application will be reviewed and you will hear back within 2 weeks</li>
              </ul>
            </div>
            <button
              onClick={() => setStarted(true)}
              style={{
                padding: '12px 32px',
                background: '#c9952a',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Start Application →
            </button>
          </div>
        ) : (
          <PIFForm fund={fund} stream={stream} rubric={rubric} />
        )}
      </div>
    </div>
  );
}
